import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Camera } from "lucide-react";
import PageShell from "./PageShell";
import { Reveal, ChapterHeading } from "../components/landing/Reveal";

const GALLERY_PHOTOS = [
  {
    src: "/photos/awards.jpg",
    alt: "CommunityVotes Red Deer 2024 awards — Gold Winner Fitness Centres and Gyms, Bronze Winner Personal Trainer",
    caption: "CommunityVotes Red Deer 2024 — Gold & Bronze",
  },
  {
    src: "/photos/hero.jpg",
    alt: "Member showing off a Workout Like a Girl tank in the Me 1st studio",
    caption: "Workout like a girl",
  },
  {
    src: "/photos/client-transformation.jpg",
    alt: "Real Me 1st client transformation from July to October 2025",
    caption: "Real client result — 3 months",
  },
  {
    src: "/photos/tammy.jpg",
    alt: "Tammy Porter, coach at Me 1st Fitness & Coaching",
    caption: "Coach Tammy",
  },
  {
    src: "/photos/transformation-2.jpg",
    alt: "Me 1st client before and after transformation, flexing and smiling",
    caption: "Client transformation — feeling strong",
  },
  {
    src: "/photos/tammy-flex.jpg",
    alt: "Coach Tammy flexing in the studio under the Workout Like a Girl sign",
    caption: "Workout like a girl",
  },
  {
    src: "/photos/clients.jpg",
    alt: "Clients training together at the Me 1st Fitness studio",
    caption: "The studio crew",
  },
  {
    src: "/photos/transformation-3.jpg",
    alt: "Me 1st client transformation from May 1 to October 28, 2025",
    caption: "Client transformation — May to October 2025",
  },
  {
    src: "/photos/transformation-4.jpg",
    alt: "Me 1st client before and after transformation, back view",
    caption: "Client transformation — real progress",
  },
  {
    src: "/photos/outdoors.jpg",
    alt: "Coach Tammy enjoying a river hike in the forest",
    caption: "Strong for life — outdoors too",
  },
  {
    src: "/photos/training-1.jpg",
    alt: "Client doing dumbbell lateral raises in the Me 1st studio",
    caption: "Putting in the work",
  },
  {
    src: "/photos/training-2.jpg",
    alt: "Client taking a water break mid-session at Me 1st Fitness",
    caption: "Hydration break",
  },
  {
    src: "/photos/training-3.jpg",
    alt: "Client holding a push-up position during a Me 1st session",
    caption: "Strong at every age",
  },
  {
    src: "/photos/transformation-5.jpg",
    alt: "Me 1st client before and after transformation, front and back",
    caption: "Client transformation — front and back",
  },
  {
    src: "/photos/transformation-6.jpg",
    alt: "Me 1st client before and after transformation, four views",
    caption: "Client transformation — real results",
  },
  {
    src: "/photos/halloween.jpg",
    alt: "Me 1st Fitness group dressed as witches for a Halloween workout outdoors",
    caption: "Halloween workout with the crew",
  },
  {
    src: "/photos/flex-2.jpg",
    alt: "Client flexing proudly after a session at Me 1st Fitness",
    caption: "Feeling strong",
  },
  {
    src: "/photos/cert-herbal.jpg",
    alt: "Coach Tammy holding her Wild Rose College of Herbal Medicine certificate",
    caption: "Coach Tammy — Practical Herbalist",
  },
  {
    src: "/photos/transformation-7.jpg",
    alt: "Me 1st client before and after transformation wearing an I pushed, I fought, I won tank",
    caption: "I pushed. I fought. I won.",
  },
];

const GalleryPage = () => {
  const [selected, setSelected] = useState(null);

  return (
    <PageShell title="Gallery">
      <section className="py-16 sm:py-24 bg-mint" data-testid="gallery-section">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
            <ChapterHeading number="09" label="Gallery" title="Inside Me 1st" />
            <Reveal delay={0.15}>
              <p className="max-w-sm text-charcoal/60 leading-relaxed lg:text-right">
                Real people, real workouts, real results — a look inside the Me 1st Fitness &amp;
                Coaching community in Red Deer.
              </p>
            </Reveal>
          </div>

          <div className="columns-2 md:columns-3 gap-4 [column-fill:balance]" data-testid="gallery-grid">
            {GALLERY_PHOTOS.map((p, i) => (
              <Reveal key={p.src} delay={0.05 * i} y={24} className="mb-4 break-inside-avoid">
                <button
                  onClick={() => setSelected(p)}
                  data-testid={`gallery-item-${i}`}
                  className="group relative block w-full overflow-hidden rounded-2xl focus:ring-2 focus:ring-brand"
                  aria-label={`View photo: ${p.caption}`}
                >
                  <img
                    src={p.src}
                    alt={p.alt}
                    loading="lazy"
                    className="w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-charcoal/0 group-hover:bg-charcoal/35 transition-colors duration-500 flex items-end">
                    <span className="m-4 inline-flex items-center gap-2 text-sand text-xs font-bold uppercase tracking-[0.15em] opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                      <Camera size={14} /> {p.caption}
                    </span>
                  </div>
                </button>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.2}>
            <p className="mt-10 text-center text-sm text-charcoal/50" data-testid="gallery-more-note">
              More photos from classes and coaching sessions are on the way.
            </p>
          </Reveal>
        </div>
      </section>

      <AnimatePresence>
        {selected && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[100] bg-charcoal/90 backdrop-blur-sm flex items-center justify-center p-5"
            onClick={() => setSelected(null)}
            data-testid="gallery-lightbox"
          >
            <motion.figure
              initial={{ scale: 0.92, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="max-w-3xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={selected.src}
                alt={selected.alt}
                className="w-full max-h-[80vh] object-contain rounded-2xl"
              />
              <figcaption className="mt-4 text-center text-sand/80 font-medium">
                {selected.caption}
              </figcaption>
            </motion.figure>
            <button
              onClick={() => setSelected(null)}
              data-testid="gallery-lightbox-close"
              aria-label="Close photo"
              className="absolute top-5 right-5 w-12 h-12 rounded-full bg-sand text-charcoal flex items-center justify-center hover:bg-brand transition-colors duration-300"
            >
              <X size={22} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </PageShell>
  );
};

export default GalleryPage;
